import { useState } from 'react';
import { TT } from '@features/ai-prompting/terminalTokens';

/**
 * The two-zone shell shared by all six rep formats (spec Part 2): a fixed
 * rail (context) + flex work area, in the exact vertical order the spec
 * mandates. This is the ONE place that lays out that structure — every
 * format component only supplies the slot content (surface, submitRow,
 * trace, result), never its own wrapper, so acceptance criterion 7 ("all
 * six formats share the same rail, header, trace, result model") holds by
 * construction rather than by convention.
 */
const RAIL_WIDTH = 300;
const COLLAPSE_QUERY = '(max-width: 1100px)';

const RailSection = ({ label, children }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{
      fontFamily: TT.fontMono, fontSize: 10, color: TT.textDim,
      textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
    }}>{'// '}{label}</div>
    {children}
  </div>
);

const DimensionChips = ({ dimensions }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
    {dimensions.map(d => (
      <span key={d} style={{
        fontFamily: TT.fontMono, fontSize: 11, color: TT.textMuted,
        border: `1px solid ${TT.border}`, borderRadius: 99, padding: '3px 10px',
      }}>{d}</span>
    ))}
  </div>
);

const RevealedCriteria = ({ items }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    {items.map((c, i) => (
      <div key={i} style={{ display: 'flex', gap: 6, fontFamily: TT.fontMono, fontSize: 11.5 }}>
        <span style={{ color: c.passed ? TT.green : '#ef4444', flexShrink: 0 }}>{c.passed ? '✓' : '✗'}</span>
        <span style={{ color: TT.textMuted }}>{c.name}</span>
      </div>
    ))}
  </div>
);

const RailContent = ({ rail }) => {
  const {
    lessonName, tags = [], principle, goal, constraints = [],
    dimensions = [], needsHiddenCases, revealedCriteria,
  } = rail;

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: TT.fontMono, fontSize: 11, color: TT.textDim }}>{lessonName}</div>
          {tags.map(t => (
            <span key={t} style={{ fontFamily: TT.fontMono, fontSize: 9.5, color: TT.orange, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t}</span>
          ))}
        </div>
        {principle && (
          <p style={{ fontFamily: TT.fontSans, fontSize: 12, color: TT.textMuted, fontStyle: 'italic', lineHeight: 1.5, margin: '8px 0 0' }}>
            "{principle}"
          </p>
        )}
      </div>

      {goal && (
        <RailSection label="goal">
          <p style={{ fontFamily: TT.fontSans, fontSize: 12.5, color: TT.text, lineHeight: 1.6, margin: 0, whiteSpace: 'pre-line' }}>{goal}</p>
        </RailSection>
      )}

      {constraints.length > 0 && (
        <RailSection label="constraints">
          <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {constraints.map((c, i) => (
              <li key={i} style={{ fontFamily: TT.fontSans, fontSize: 12, color: TT.textMuted, lineHeight: 1.5 }}>{c}</li>
            ))}
          </ul>
        </RailSection>
      )}

      {needsHiddenCases && (
        <RailSection label="note">
          <p style={{ fontFamily: TT.fontMono, fontSize: 11.5, color: TT.amber, lineHeight: 1.6, margin: 0 }}>
            your prompt will be tested against 3 inputs you will not see
          </p>
        </RailSection>
      )}

      {revealedCriteria ? (
        <RailSection label="evaluated on">
          <RevealedCriteria items={revealedCriteria} />
        </RailSection>
      ) : dimensions.length > 0 && (
        <RailSection label="evaluated on">
          <DimensionChips dimensions={dimensions} />
        </RailSection>
      )}
    </>
  );
};

// Sandbox part 4: centering the idle single column (part 3) was wrong —
// it split the dead space into two voids and detached the work column
// from the rail. Removed. The work area now starts adjacent to the rail
// with a normal gutter and fills the available width out to a matching
// right margin — no cap, no centering. Only the split left column keeps a
// fixed width ("the editor keeps its current width and never moves"),
// unchanged from part 2/3. Below 1400px, both columns are plain blocks
// that stack in DOM order — same collapse as before.
const WORK_GUTTER = 48; // "a normal gutter" — replaces the old 24/28 padding
const SPLIT_LEFT_WIDTH = 760;
const SPLIT_QUERY = '(min-width: 1400px)';

export const SandboxShell = ({
  accent = TT.orange,
  topBar,
  rail,
  formatLabel,
  repTitle,
  leftColumn,
  rightColumn,
  // Explicit, not inferred from rightColumn's truthiness: Build's right
  // column is genuinely empty until submission, but the grid must already
  // be active so the left column's width never shifts once content
  // appears there ("the editor... never moves"). Compare doesn't pass
  // this at all — single full-width column, unchanged, full stop.
  splitLayout = false,
  stickyLeft = false,
}) => {
  const [railOpen, setRailOpen] = useState(false);
  const hasSplit = splitLayout;

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: TT.bg, display: 'flex', flexDirection: 'column',
      fontFamily: TT.fontSans,
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
      backgroundSize: '22px 22px',
      '--ps-accent': accent,
    }}>
      <style>{`
        .sandbox-rail { display: block; }
        .sandbox-rail-disclosure { display: none; }
        @media ${COLLAPSE_QUERY} {
          .sandbox-shell-body { flex-direction: column !important; }
          .sandbox-rail { width: 100% !important; border-right: none !important; border-bottom: 1px solid ${TT.border}; }
          .sandbox-rail-fixed { display: none !important; }
          .sandbox-rail-disclosure { display: block !important; }
        }
        @media ${SPLIT_QUERY} {
          .sandbox-work-area.has-split {
            display: grid;
            grid-template-columns: ${SPLIT_LEFT_WIDTH}px 1fr;
            gap: 32px;
            align-items: start;
          }
          .sandbox-work-area.has-split.sticky-left .sandbox-col-left {
            position: sticky;
            top: 20px;
          }
        }
      `}</style>

      {/* rail-clearance (defined in index.css) insets visible content past
          the app's floating sidebar without shifting this element's own
          background — that background element is the OUTER div above,
          which must stay full-bleed underneath the sidebar. */}
      <div className="rail-clearance" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {topBar}

        <div className="sandbox-shell-body" style={{ flex: 1, display: 'flex', width: '100%' }}>
          <div className="sandbox-rail" style={{
            width: RAIL_WIDTH, flexShrink: 0, borderRight: `1px solid ${TT.border}`,
            padding: '20px 20px', boxSizing: 'border-box',
          }}>
            <div className="sandbox-rail-disclosure">
              <button
                onClick={() => setRailOpen(o => !o)}
                style={{
                  width: '100%', textAlign: 'left', background: 'transparent', border: `1px solid ${TT.border}`,
                  borderRadius: TT.radius.sm, padding: '8px 12px', color: TT.textMuted,
                  fontFamily: TT.fontMono, fontSize: 11, cursor: 'pointer', marginBottom: railOpen ? 14 : 0,
                }}
              >
                {railOpen ? '▾' : '▸'} context
              </button>
              {railOpen && <RailContent rail={rail} />}
            </div>
            <div className="sandbox-rail-fixed">
              <RailContent rail={rail} />
            </div>
          </div>

          <div style={{ flex: 1, padding: `24px ${WORK_GUTTER}px 48px`, boxSizing: 'border-box' }}>
            {/* The work area owns the title — the rail no longer repeats
                it (part 4 item 5). Fills the available width; only prose
                inside it (each format's own instruction/result text) caps
                its own measure — this header is a heading, not prose. */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
                <span style={{
                  fontFamily: TT.fontMono, fontSize: 10.5, fontWeight: 800, color: accent,
                  textTransform: 'uppercase', letterSpacing: '0.08em',
                }}>{formatLabel}</span>
              </div>
              <h1 style={{ fontFamily: TT.fontDisplay, fontSize: 22, fontWeight: 700, color: TT.text, margin: 0 }}>
                {repTitle}
              </h1>
            </div>

            <div className={`sandbox-work-area ${hasSplit ? 'has-split' : ''} ${stickyLeft ? 'sticky-left' : ''}`}>
              <div className="sandbox-col-left">{leftColumn}</div>
              {hasSplit && <div className="sandbox-col-right">{rightColumn}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SandboxShell;
